/**
 * The bulk "Set service period" dialog — a CONSTRUCTIVE third bulk action on PoolUsersPage's page-scoped
 * multi-select bar. It applies ONE service period (dates + renew decision) to every currently-selected
 * eligible user via sequential, client-side, one-POST-per-email calls to POST /pool/periods (there is no
 * server batch endpoint for period creation), synthesizing the per-email outcome map itself and rendering
 * an aggregate results panel.
 *
 * Deliberately NOT a case of BulkForceDialog: creating a period is a plain DB write that ARMS the sweep
 * engine — it never calls Zoom itself — so this dialog inherits NONE of the danger dialog's write-mode
 * tri-state gating, dry-run, typed-count ceremony, or Zoom-reachability warnings. What it DOES share
 * (result bucketing/ranking, the renew sub-form, the payload builder) is shared at the seam, via
 * forceOutcome/RenewDecisionFields/renewValidation, not by overloading the danger dialog.
 *
 * DA#1 (the load-bearing risk): setting a period on the default renew=undecided silently ARMS an
 * automatic license removal at the end date, months later, with no dry-run to catch it. This dialog's
 * ONLY safeguard is the confirm gate, so the arming-honesty line (renew-choice-dynamic, always visible)
 * states that consequence in plain language in every confirm state.
 *
 * DA#4: the `users` prop derives from selectedEmails, which a background content-signature change can
 * reset while this dialog is open — so it is SNAPSHOT once at mount (§2.2) and the prop is never re-read.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { ApiRequestError } from "@/api/client";
import { Button, DateRangePicker, Modal, StatusPill } from "@/components/ui";
import { RenewDecisionFields } from "@/features/pool/RenewDecisionFields";
import { bucket, outcomeText, rank } from "@/features/pool/forceOutcome";
import {
  buildCreatePeriodBody,
  validateRange,
  validateRenewNo,
  validateRenewYes,
  type RenewChoice,
} from "@/features/pool/renewValidation";
import { poolKeys, useBulkSetPeriod } from "@/features/pool/hooks";
import { oneYearFrom, todayIso } from "@/features/pool/dateUtils";
import type { PoolExpiryAction, PoolUser } from "@/api/types";
import type { TKey } from "@/i18n/keys";

export interface BulkSetPeriodDialogProps {
  /** displayedUsers.filter(u => selectedEmails.has(u.email)) at mount — snapshot once, never re-read. */
  users: PoolUser[];
  onClose: () => void;
}

type Phase = "confirm" | "running" | "done";

export function BulkSetPeriodDialog({ users, onClose }: BulkSetPeriodDialogProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const mut = useBulkSetPeriod();

  // DA#4 — frozen at open. A background reset of selectedEmails (which empties the `users` prop) must NOT
  // change the confirm count or the run target once this dialog is up. Everything below reads `snapshot`.
  const [snapshot] = useState(() => users);

  const [phase, setPhase] = useState<Phase>("confirm");
  const [start, setStart] = useState(todayIso());
  const [end, setEnd] = useState(oneYearFrom(todayIso()));
  // NULL/undecided default — the locked product rule (matches PeriodModal's create branch).
  const [renewChoice, setRenewChoice] = useState<RenewChoice>("undecided");
  const [expiryAction, setExpiryAction] = useState<PoolExpiryAction>("license_only");
  const [graceDays, setGraceDays] = useState("");
  const [nextEnd, setNextEnd] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);
  const [results, setResults] = useState<Record<string, string>>({});
  const [partialFailure, setPartialFailure] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Defensive only (§7): the bar mounts this dialog solely when count>0, and the snapshot is frozen, so an
  // empty snapshot can't happen in practice — but if it somehow raced to zero, close rather than strand.
  useEffect(() => {
    if (snapshot.length === 0) onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (snapshot.length === 0) return null;

  /** The arming-honesty copy, DYNAMIC on the renew selection (DA#1). Rendered in EVERY confirm state. */
  function armingKey(): TKey {
    if (renewChoice === "undecided") return "pool.users.bulkSetPeriod.arming.undecided";
    if (renewChoice === "yes") return "pool.users.bulkSetPeriod.arming.renewYes";
    if (expiryAction === "grace_2stage") return "pool.users.bulkSetPeriod.arming.expiryGrace";
    if (expiryAction === "disassociate_now")
      return "pool.users.bulkSetPeriod.arming.expiryDisassociate";
    return "pool.users.bulkSetPeriod.arming.expiryLicenseOnly";
  }

  function validate(): boolean {
    setError(null);
    // A strict superset of PeriodModal's create branch (which leans on the picker): one bad range would
    // poison N rows here, so the both-present + end≥start guard runs before any request fires.
    const dk = validateRange(start, end);
    if (dk) {
      setError(t(dk));
      return false;
    }
    if (renewChoice === "no") {
      const k = validateRenewNo(expiryAction, graceDays);
      if (k) {
        setError(t(k));
        return false;
      }
    }
    // 30/7 — a bulk Yes now sets everyone's NEXT round too (same one-save rule as the single form), so
    // the same missing/backwards guard has to run here or N rows 422 one by one mid-loop.
    if (renewChoice === "yes") {
      const k = validateRenewYes(end, nextEnd);
      if (k) {
        setError(t(k));
        return false;
      }
    }
    return true;
  }

  function finish(merged: Record<string, string>) {
    setResults(merged);
    setPhase("done");
    // Created rows now carry a period_id → they become select-disabled on the grid refetch.
    void qc.invalidateQueries({ queryKey: ["pool", "users"] });
    void qc.invalidateQueries({ queryKey: poolKeys.dashboard });
  }

  async function run() {
    setPhase("running");
    const merged: Record<string, string> = {};
    const total = snapshot.length;
    for (let i = 0; i < total; i++) {
      const target = snapshot[i];
      if (!target) continue; // unreachable (i < total) — satisfies noUncheckedIndexedAccess
      const email = target.email;
      setProgress({ current: i + 1, total });
      try {
        await mut.mutateAsync(
          buildCreatePeriodBody(email, {
            start,
            end,
            renewChoice,
            expiryAction,
            graceDays,
            nextEnd,
          }),
        );
        merged[email] = "ok_created";
      } catch (e) {
        // Classify explicitly for THIS single-create endpoint — do NOT reuse bulk-force's
        // request-level classifier, whose buckets are wrong here (it lets 503 fall through and
        // halts on a plain 400).
        const status = e instanceof ApiRequestError ? e.error.status : null;
        if (status === null || status >= 500) {
          // Network failure or ANY 5xx (incl. 503) → the backend is unhealthy; the next N calls will
          // likely fail too. HALT rather than hammer it. THIS email is left unrecorded (its true
          // state is unknown); rows already completed are kept.
          setPartialFailure(true);
          finish(merged);
          return;
        }
        if (status === 409) {
          // A period appeared for this email since page-load (race) — NEVER overwrite an existing open
          // period in bulk (locked rule). Record as a skip and keep going.
          merged[email] = "skip_has_open_period";
          continue;
        }
        // Any other 4xx (400 / 422 / …) — this specific row is bad; the others are independent DB writes.
        merged[email] = "failed_create";
      }
    }
    finish(merged);
  }

  const entries = Object.entries(results);
  const counts = entries.reduce(
    (acc, [, code]) => {
      acc[bucket(code)] += 1;
      return acc;
    },
    { ok: 0, skip: 0, failed: 0 },
  );
  const sortedEntries = [...entries].sort(
    ([, a], [, b]) => rank(a) - rank(b) || a.localeCompare(b),
  );

  return (
    <Modal
      open
      // No-op close while running (same guard as BulkForceDialog) — prevents close-during-run + double-submit.
      onClose={phase === "running" ? () => undefined : onClose}
      title={t("pool.users.bulkSetPeriod.confirmTitle", { count: snapshot.length })}
      size="md"
      footer={
        phase === "confirm" ? (
          <>
            <Button variant="secondary" onClick={onClose}>
              {t("action.cancel")}
            </Button>
            {/* Constructive — primary fill, NOT danger, and NO typed-count gate. */}
            <Button
              variant="primary"
              onClick={() => {
                if (validate()) void run();
              }}
            >
              {t("pool.users.bulkSetPeriod.confirmButton")}
            </Button>
          </>
        ) : phase === "done" ? (
          <Button variant="secondary" onClick={onClose}>
            {t("action.close")}
          </Button>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-4">
        {phase === "confirm" && (
          <>
            <p className="text-(length:--text-sm) text-(--text)">
              {t("pool.users.bulkSetPeriod.confirmBody")}
            </p>

            <DateRangePicker
              value={{ start, end }}
              onChange={(r) => {
                setStart(r.start);
                setEnd(r.end);
              }}
              aria-label={`${t("pool.period.startLabel")} / ${t("pool.period.endLabel")}`}
            />

            <RenewDecisionFields
              periodEnd={end}
              nextEnd={nextEnd}
              onNextEnd={setNextEnd}
              renewChoice={renewChoice}
              onRenewChoice={setRenewChoice}
              expiryAction={expiryAction}
              onExpiryAction={setExpiryAction}
              graceDays={graceDays}
              onGraceDays={setGraceDays}
            />

            {/* DA#1 — the arming-honesty line: always visible, dynamic on the renew choice, stating what
                happens to these users at the end date. This confirm dialog is the ONLY gate (no dry-run),
                so the consequence is stated in plain language, in a warn accent, never muted. */}
            <p
              role="note"
              className="rounded-(--radius) border border-(--warn) bg-(--warn-subtle) px-3 py-2 text-(length:--text-sm) font-medium text-(--warn)"
            >
              {t(armingKey(), { days: graceDays })}
            </p>

            {/* DA#2 — count + exact chosen range, strong weight, directly above the confirm button so the
                uniform application (and the dates) are impossible to submit without seeing. */}
            <p className="text-(length:--text-sm) font-semibold text-(--text-strong)">
              {t("pool.users.bulkSetPeriod.dateRangeSummary", {
                count: snapshot.length,
                start,
                end,
              })}
            </p>

            {/* Full scrollable email list — no "and N more" truncation (shared, action-agnostic label). */}
            <div className="flex flex-col gap-1.5">
              <span className="text-(length:--text-xs) font-medium text-(--text-muted)">
                {t("pool.users.bulk.emailListLabel", { count: snapshot.length })}
              </span>
              <ul className="max-h-48 overflow-y-auto rounded-(--radius) border border-(--border) bg-(--surface-sunken) p-2 text-(length:--text-sm)">
                {snapshot.map((u) => (
                  <li key={u.email} className="px-1 py-0.5 text-(--text)">
                    {u.email}
                  </li>
                ))}
              </ul>
            </div>

            {error && (
              <p role="alert" className="text-(length:--text-sm) text-(--danger)">
                {error}
              </p>
            )}
          </>
        )}

        {phase === "running" && progress && (
          <p className="text-(length:--text-sm) text-(--text-muted)" role="status">
            {t("pool.users.bulkSetPeriod.progressLabel", {
              current: progress.current,
              total: progress.total,
            })}
          </p>
        )}

        {phase === "done" && (
          <div className="flex flex-col gap-2 rounded-(--radius) border border-(--border) bg-(--surface-sunken) p-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-(length:--text-sm) font-semibold text-(--text-strong)">
                {t("pool.period.force.resultTitle")}
              </p>
              <StatusPill tone={counts.ok > 0 ? "ok" : counts.failed > 0 ? "danger" : "neutral"}>
                {counts.ok > 0
                  ? t("pool.users.bulkSetPeriod.createdBadge")
                  : t("pool.users.bulkSetPeriod.noneCreatedBadge")}
              </StatusPill>
            </div>

            {partialFailure && (
              <p role="alert" className="text-(length:--text-sm) text-(--danger)">
                {t("pool.users.bulkSetPeriod.partialFailureNote")}
              </p>
            )}

            <p className="text-(length:--text-sm) text-(--text)">
              {t("pool.users.bulk.resultSummary", {
                ok: counts.ok,
                skipped: counts.skip,
                failed: counts.failed,
              })}
            </p>

            {sortedEntries.length > 0 && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  className="self-start"
                  onClick={() => setDetailsOpen((v) => !v)}
                >
                  {detailsOpen
                    ? t("pool.users.bulk.hideDetailsButton")
                    : t("pool.users.bulk.showDetailsButton")}
                </Button>
                {detailsOpen && (
                  <ul className="flex max-h-64 flex-col gap-1 overflow-y-auto text-(length:--text-sm)">
                    {sortedEntries.map(([email, code]) => (
                      <li key={email} className="flex items-center justify-between gap-3">
                        <span className="text-(--text)">{email}</span>
                        <span className="text-(--text-muted)">{outcomeText(code, t)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
